<template>
  <div class="mini-chat-container" ref="chatContainer">
    <template v-if="showingFlashcards">
      <chat-flashcards
        :chatID="chatID"
        @close="showingFlashcards = false"
        @showChat="showChat()"
        @showArticle="showArticle()"
      />
    </template>
    <template v-else-if="showingArticle">
      <div class="mini-chat-messages">
        <div class="card my-2">
          <div class="text-start p-1">
            <span v-html="marked(article.text)"></span>
          </div>
        </div>
        <div class="card my-2" v-for="sentence in article.sentences" v-bind:key="sentence.id">
          <div class="text-start p-1">
            <i class="action-icon bi bi-play-circle action-icon pe-1 fs-5" @click="play_sentence(sentence)" v-c-tooltip="t('chat.play-sentence')" />
            <i class="action-icon bi bi-translate action-icon pe-1 fs-5" @click="translate_sentence(sentence)" v-c-tooltip="t('chat.translate-sentence')" />
            <i class="action-icon bi bi-chat action-icon pe-1 fs-5" @click="add_sentence_to_chat(sentence)" v-c-tooltip="t('chat.add-sentence-to-chat')" />
            &nbsp;
            <span :class="['text-start', 'breakable', authStore.fontClass]" v-html="sentence.annotation"></span>
            <template v-if="sentence.show_translation">
              <hr />
              <div class="text-start p-1" v-html="sentence.translation"></div>
            </template>
          </div>
        </div>
      </div>
      <div class="mt-2 text-center d-flex justify-content-center">
        <div v-if="isMenuVisible" class="popout-menu">
          <ul class="text-start">
            <li @click="menuAction('chat.zoom-in')"><i class="bi-zoom-in" />{{ t('chat.zoom-in') }}</li>
            <li @click="menuAction('chat.zoom-out')"><i class="bi-zoom-out" />{{ t('chat.zoom-out') }}</li>
            <li @click="menuAction('chat.menu-5-example-sentences')">{{ t('chat.menu-5-example-sentences') }}</li>
            <li @click="menuAction('chat.menu-write-a-short-story')">{{ t('chat.menu-write-a-short-story') }}</li>
            <li @click="menuAction('chat.menu-quiz-me')">{{ t('chat.menu-quiz-me') }}</li>
          </ul>
        </div>
        <i class="bi bi-plus-circle action-icon float-start pe-3 fs-5 disabled-icon"  />
        <i class="bi bi-translate action-icon float-start pe-3 fs-5 disabled-icon"  />
        <i class="bi bi-lightbulb action-icon float-start pe-3 fs-5 disabled-icon" />
        <i class="bi bi-mic action-icon float-start pe-3 fs-5 disabled-icon" />
        <i class="bi bi-chat action-icon float-start pe-3 fs-5" @click="showChat()" v-c-tooltip="t('chat.show-chat')" v-if="readingTogether && showingArticle" />
        <i class="bi bi-collection action-icon float-start pe-3 fs-5" @click="showFlashcards()" v-if="hasFlashcards && !showingFlashcards" />
      </div>
    </template>
    <template v-else>
      <div class="mini-chat-messages" ref="chatMessages">
        <chat-message v-for="message in messages" :message="message" v-bind:key="message.messageID" @deleteMessage="deleteMessage"  :popoverVisible="message.popoverVisible"/>
      </div>
      <div v-show="recording" id="outbound_message_cover" ref="outbound_message_cover" @click="stop_recording">
        <AVMedia v-if="prettyStream" :media="prettyStream" type="frequ" frequ-direction="mo" line-color="#147efb" line-width=1 canv-width="200" canv-height="100" />
      </div>
      <div ref="chatInputContainer" class="mini-chat-input">
        <div v-if="show_suggestion_pane" class="suggestion-pane p-3" style="width: 95%">
          <h5 class="suggestion-heading mb-3">{{ t('chat.suggested-messages') }}</h5>
          <div class="row">
            <div class="col-12">
              <div class="row p-1">
                <div v-for="suggestion in suggestions" v-bind:key="suggestion.id" class="suggestion-card col-xs-12 col-sm-6 mb-2">
                  <div class="suggestion-card-body p-3" @click="use_suggestion(suggestion)">
                    <div>
                      <span v-if="suggestion.annotation" v-html="suggestion.annotation"></span>
                      <span v-else>{{ suggestion.text }}</span>
                    </div>
                    <div v-if="suggestion.translation" class="suggestion-translation" v-html="suggestion.translation"></div>
                  </div>
                </div> <!-- end suggestion for loop-->
              </div>
            </div><!-- end md-11 -->
          </div>
        </div> <!-- end show suggestions -->
        <div  class="row align-items-center">
          <div  class="col-11">
            <div
                class="chat-input h-100"
                contenteditable="true"
                ref="chatInput"
                @keydown="handleKeyDown"
                @keydown.enter.prevent
                @focus="delayScrollToBottom()"
                :data-placeholder="t('chat.type-your-message-here--')"
                @input="updateOutboundMessage"
            />
          </div>
          <div class="col-1">
            <i class="bi bi-send-fill action-icon float-end fs-4" :class="{ 'disabled-icon': isMessageEmpty }" @click="isMessageEmpty ? null : send_message()" />
          </div>
        </div>
      </div>
      <div class="mt-2 text-center d-flex justify-content-center">
        <div v-if="isMenuVisible" class="popout-menu">
          <ul class="text-start">
            <li @click="menuAction('chat.zoom-in')"><i class="bi-zoom-in" />{{ t('chat.zoom-in') }}</li>
            <li @click="menuAction('chat.zoom-out')"><i class="bi-zoom-out" />{{ t('chat.zoom-out') }}</li>
            <li @click="menuAction('chat.menu-5-example-sentences')">{{ t('chat.menu-5-example-sentences') }}</li>
            <li @click="menuAction('chat.menu-write-a-short-story')">{{ t('chat.menu-write-a-short-story') }}</li>
            <li @click="menuAction('chat.menu-quiz-me')">{{ t('chat.menu-quiz-me') }}</li>
          </ul>
        </div>
        <i class="bi bi-plus-circle action-icon float-start pe-3 fs-5" @click="toggleMenu" />
          <i v-if="profileStore.profile.auto_play" class="bi bi-volume-up-fill action-icon float-start pe-3 fs-5" @click="toggle_audio()" />
          <i v-else class="bi bi-volume-mute-fill action-icon float-start pe-3 fs-5" @click="toggle_audio()" />
          <i class="bi bi-translate action-icon float-start pe-3 fs-5" :class="{ 'disabled-icon': isMessageEmpty }" @click="isMessageEmpty ? null : translate_message()" />
          <i class="bi bi-lightbulb action-icon float-start pe-3 fs-5" @click="show_suggestions()" />
          <i v-if="!recording" @click="record_audio" class="bi bi-mic action-icon float-start pe-3 fs-5" v-c-tooltip="t('chat.record-a-message')" ></i>
          <i v-if="recording" @click="stop_recording" class="bi bi-square action-icon float-start pe-3 fs-5" v-c-tooltip="t('chat.stop-recording')"></i>
          <i class="bi bi-file-text action-icon float-start pe-3 fs-5" @click="showArticle()" v-c-tooltip="t('chat.show-article')" v-if="readingTogether && !showingArticle" />
          <i class="bi bi-collection action-icon float-start pe-3 fs-5" @click="showFlashcards()" v-if="hasFlashcards && !showingFlashcards" />


        </div>
      </template>
    </div>
  </template>

  <script setup>
  import { ref, onMounted, computed, nextTick, inject } from 'vue'
  import { onBeforeRouteLeave } from 'vue-router'
  import { fetchWrapper, getBaseUrl, getWebsocket } from '@/helpers/fetch-wrapper'
  import { useAuthStore } from '@/stores/auth.store'
  import { useProfileStore } from '@/stores/profile.store'
  const profileStore = useProfileStore()
  import { useI18n } from 'vue-i18n'
  import ChatMessage from '@/components/ChatMessage.vue'
  import ChatFlashcards from '@/components/ChatFlashcards.vue'
  import { isApp } from '@/helpers/utils.js'
  import { marked } from 'marked'
  import { AVMedia } from 'vue-audio-visual'
  const { t } = useI18n()

  // Define emits
  const emit = defineEmits(['close'])

  const chatMessages = ref(null)
  const chatContainer = ref(null)
  const chatInputContainer = ref(null)
  const reward_notifications = inject('reward_notifications')
  const isMenuVisible = ref(false)
  const show_suggestion_pane = ref(false)
  const suggestions = ref([])
  const authStore = ref(false)
  const outbound_message_cover = ref(false)
  const prettyStream = ref(null)
  const hasFlashcards = ref(true)
  authStore.value = useAuthStore()

  function toggle_audio() {
    profileStore.changeAutoPlay(!profileStore.profile.auto_play)
    if (!profileStore.profile.auto_play) {
      audiomanager.pause()
    }
  }

  function scrollToBottom() {
    nextTick(() => {
      if (chatMessages.value) {
        chatMessages.value.scrollTop = chatMessages.value.scrollHeight
        console.log('change chatmessages scroll top')
      }
      // chatContainer.value.scrollTop = chatContainer.value.scrollHeight
      // console.log('change chatcontainer scroll top')
    })
  }
  async function delayScrollToBottom() {
    setTimeout(scrollToBottom, 1000)
  }

  if (isApp) {
    // window.visualViewport.onresize = scrollToBottom
    // TODO put back in here
  }
  window.visualViewport.onresize = scrollToBottom

  // props include game_sessionID
  const props = defineProps({
    templateID: {
      type: [Number, String],
      required: true
    },
    game_sessionID: {
      type: [Number, String],
      required: false
    },
    curriculum_conceptID: {
      type: [Number, String],
      required: false
    },
    curriculum_topicID: {
      type: [Number, String],
      required: false
    },
    curriculum_unitID: {
      type: [Number, String],
      required: false
    },
    flashcard_entryID: {
      type: [Number, String],
      required: false
    },
    chatID: {
      type: [Number, String],
      required: false
    },
    vocab_entryID: {
      type: [Number, String],
      required: false
    },
    recent_vocabID: {
      type: [Number, String],
      required: false
    },
  })
  // State management
  let current_message = null
  const outbound_message = ref('')
  const messages = ref([])
  const auto_play_self = ref(false)
  const chatID = ref(null)
  const chatInput = ref(null)
  const article = ref(null)
  const displayMode = ref("chat")  // can be chat, article, or flashcards
  const showingArticle = computed(() => {
    return displayMode.value === 'article'
  })
  const showingFlashcards = computed(() => {
    return displayMode.value === 'flashcards'
  })
  const learning_language = ref(false)
  const codec = ref(null)
  const audiomanager = inject('audiomanager')
  const recording = ref(false)
  let mediaRecorder = null
  let audioCtx = null
    async function menuAction(action) {
      if (action === 'chat.menu-5-example-sentences') {
        outbound_message.value = t('chat.menu-5-example-sentences')
        await send_message()
      } else if (action === 'chat.menu-write-a-short-story') {
        outbound_message.value = t('chat.menu-write-a-short-story')
        await send_message()
      } else if (action === 'chat.menu-quiz-me') {
        outbound_message.value = t('chat.menu-quiz-me')
        await send_message()
      } else if (action === 'chat.zoom-in') {
        const chatMessagesElement = chatMessages.value
        if (chatMessagesElement) {
          console.log('zoom_in found element')
          const currentFontSize = window.getComputedStyle(chatMessagesElement).fontSize
          const currentFontSizeInPx = parseFloat(currentFontSize)
          const newFontSize = currentFontSizeInPx + 8
          console.log('zoom_in found element', currentFontSize, currentFontSizeInPx, newFontSize)
          chatMessagesElement.style.fontSize = `${newFontSize}px`
        }
      } else if (action === 'chat.zoom-out') {
        const chatMessagesElement = chatMessages.value
        if (chatMessagesElement) {
          const currentFontSize = window.getComputedStyle(chatMessagesElement).fontSize
          const currentFontSizeInPx = parseFloat(currentFontSize)
          const newFontSize = currentFontSizeInPx - 8
          chatMessagesElement.style.fontSize = `${newFontSize}px`
        }
      }
      isMenuVisible.value = false
    }

    async function add_sentence_to_chat(sentence) {
      clearTooltips()
      setOutboundMessage(sentence.text)
      displayMode.value = 'chat'
      await send_message('explain_sentence')
    }
    async function translate_message() {
      clearTooltips()
      if (outbound_message.value === '') {
        return
      }
      const response = await fetchWrapper.post(`/translate/`, {
        chatID: chatID.value,
        text: outbound_message.value
      })
      setOutboundMessage(response.translation)
    }
    const readingTogether = computed(() => {
    if (article.value) {
      return true
    }
    return false
    })
    const deleteMessage = async (message) => {
      console.log("ok deleting message")
      await delete_message(message)
    }

    async function delete_message(message) {
      const idx = messages.value.indexOf(message)
      // delete the message and all subsequent messages
      if (message.role == 'user') {
        setOutboundMessage(message.text)
      }
      messages.value.splice(idx, messages.value.length - idx)
      await fetchWrapper.post(`/chat/message/delete/`, { messageID: message.messageID })
      if (message.role == 'ai') {
        await get_ai_message()
      }
    }
    const filtered_messages = computed(() => {
      return messages.value.filter((message) => {
        if (message.role == 'system') {
          return showSystemMessages.value
        }
        return true
      })
    })
    function add_message(message) {
      const { user } = useAuthStore()
      message.audio = getBaseUrl(`/tts/?messageID=${message.messageID}&token=${user.token}`)
      message.popoverVisible = false
      messages.value.push(message)
      scrollToBottom()
      /* if it's a fresh message, it won't have been grammar checked.
      we do this through an async callback so that we can get straight to
      fetching a response from the AI in parallel rather than blocking on
      the grammar check, which makes it feel much snappier. */
      if (message.fresh) {
        fetchWrapper.post(`/chat/grammar/`, { chat_messageID: message.messageID, text: message.text }).then((data) => {
          /* we can't just modify the message that we just popped, we need to
          find it in the array because that's where the reactive copy lives.
          modifying message will have weird and unstable effects. */
          for (const temp of messages.value) {
            if (temp.messageID == message.messageID) {
              temp.grammar = data.message
              break
            }
          }
        })
      }
      console.log('message', message)
      if (message.fresh && auto_play_self.value && profileStore.profile.auto_play) {
        console.log('playing message', message.audio)
        play_audio(message)
      }
      else {
        console.log('not playing message', message.audio, 'fresh', message.fresh, 'auto_play_self', auto_play_self.value, 'auto_play', profileStore.profile.auto_play)
      }
    }
    // start a remote message that tokens will be appended to
    function start_ai_message(messageID, disable_auto_play) {
      current_message = ref('')
      messages.value.push({
        role: 'ai',
        text: current_message,
        show_translation: false,
        annotation: null,
        messageID: messageID,
        grammar: null,
        translation: null,
        show: 'text',
        audio: null,
        show_vocab: false,
        vocab: [],
        playing: false,
        popoverVisible: false,
        disable_auto_play: disable_auto_play,
      })
    }
    // signal that we can now play the audio
    function end_ai_message(messageID) {
      const message = messages.value[messages.value.length - 1]
      const { user } = useAuthStore()
      message.audio = getBaseUrl(`/tts/?messageID=${messageID}&token=${user.token}`)
      if (!profileStore.profile.auto_play) {
        return
      }
      if (message.disable_auto_play) {
        return
      }
      play_audio(message)
    }
    function annotation(data) {
      const message = messages.value[messages.value.length - 1]
      message.annotation = data.annotation
      scrollToBottom()
    }
    function translation(data) {
      const message = messages.value[messages.value.length - 1]
      message.translation = data.translation
      message.show = 'translation'
    }
    async function handleKeyDown(event) {
      if (event.isComposing || event.keyCode === 229) {
        return
      }
      if (event.key === 'Enter') {
        chatInput.value.blur()
        await send_message()
      }
    }
    async function send_message(special = '') {
      console.log('enter send_message');
      const message = outbound_message.value;
      if (!message) {
        return;
      }
      setOutboundMessage(''); // Clear the message and the contenteditable div
      const response = await fetchWrapper.post(`/chat/message/create/`, { chatID: chatID.value, text: message, special: special });
      add_message(response);
      // scroll after 0.2 seconds
      await get_ai_message();
      scrollToBottom();
      // focus the input again
      if (chatInput.value) {
        chatInput.value.focus();
      }
    }

    async function get_ai_message() {
      const connection = getWebsocket(`/message/${chatID.value}/`)
      connection.onmessage = function (event) {
        const data = JSON.parse(event.data)
        console.log("got message", data)
        if (data.type == 'token') {
          current_message.value += data.token
          scrollToBottom()
        } else if (data.type == 'start_ai_message') {
          start_ai_message(data.messageID, data.disable_auto_play)
        } else if (data.type == 'end_ai_message') {
          end_ai_message(data.messageID)
          scrollToBottom()
        } else if (data.type == 'add_message') {
          add_message(data)
          scrollToBottom()
        } else if (data.type == 'annotation') {
          annotation(data)
        } else if (data.type == 'translation') {
          translation(data)
          scrollToBottom()
        } else if (data.type == 'notification') {
          // Pass a closure callback that can be used to close the MiniChat if needed
          const closureCallback = () => {
            // Call parent's handleModalDismiss method
            console.log('MiniChat closure callback called')
            emit('close')
            console.log('MiniChat closure callback emitted requestClose')
          }
          reward_notifications.value.showMessage(data.message, closureCallback)
        }
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      connection.onclose = function (event) {
        console.log('websocket closed')
        scrollToBottom()
      }
    }

    // Function to toggle the menu visibility
    function toggleMenu() {
      isMenuVisible.value = !isMenuVisible.value
    }


  // Computed property to check if the message is empty
  const isMessageEmpty = computed(() => !outbound_message.value.trim())

  function updateOutboundMessage(event) {
    outbound_message.value = event.target.innerText.trim()
    const isEmpty = event.target.textContent.trim() === '';
    if (isEmpty) {
      event.target.innerHTML = '';
    }
  }

    function play_suggestion(suggestion) {
      clearTooltips()
      const { user } = useAuthStore()
      suggestion.audio = getBaseUrl(`/tts/?lang=${learning_language.value}&text=${suggestion.text}&token=${user.token}`)
      setTimeout(() => play_audio(suggestion), 100)
    }
    function use_suggestion(suggestion) {
      setOutboundMessage(suggestion.text)
      show_suggestion_pane.value = false
    }

    async function show_suggestions() {
      clearTooltips()
      show_suggestion_pane.value = true
      suggestions.value = []
      const response = await fetchWrapper.post(`/chat/suggestion/`, {
        chatID: chatID.value,
        hint: outbound_message.value
      })
      suggestions.value = response.suggestions
      setTimeout(scrollToBottom, 200)
    }
    async function play_sentence(sentence) {
      clearTooltips()
      const { user } = useAuthStore()
      sentence.audio = getBaseUrl(`/tts/?lang=${learning_language.value}&text=${sentence.text}&token=${user.token}`)
      setTimeout(() => play_audio(sentence), 100)
    }
    function play_audio(message) {
      clearTooltips()
      wait_for_not_playing(() => {
        audiomanager.play(message.audio).then(() => {
        })
      }, 0)
    }
    function wait_for_not_playing(callback, count) {
      console.log("entering wait_for_not_playing", count)
      // if we've waited for 10 seconds, we should just give up
      if (count == 100) {
        return
      }
      if (audiomanager.playing) {
        console.log('waiting for not playing')
        setTimeout(() => wait_for_not_playing(callback, count + 1), 100)
      } else {
        callback()
      }
    }
    async function translate_sentence(sentence) {
      clearTooltips()
      if (!sentence.translation) {
        const response = await fetchWrapper.post(`/translate/`, {
          chatID: chatID.value,
          text: sentence.text,
          reverse: true
        })
        sentence.translation = response.translation
      }
      sentence.show_translation = !sentence.show_translation
    }
    function showArticle() {
      clearTooltips()
      displayMode.value = 'article'
      clearTooltips()
    }

    function showFlashcards() {
      clearTooltips()
      displayMode.value = 'flashcards'
      clearTooltips()
    }

    function showChat() {
      clearTooltips()
      displayMode.value = 'chat'
      clearTooltips()
      delayScrollToBottom()
    }

  function setOutboundMessage(value) {
    outbound_message.value = value;
    if (chatInput.value) {
      chatInput.value.innerText = value; // Update the contenteditable div
    }
  }
    function clearTooltips () {
      const tooltipsInner = document.querySelector('.tooltip-inner')
      if (tooltipsInner !== null) {
        tooltipsInner.remove()
      }
      const tooltips = document.querySelector('.tooltips')
      if (tooltips !== null) {
        tooltips.remove()
      }
      const tooltipsArrow = document.querySelector('.tooltip-arrow')
      if (tooltipsArrow !== null) {
        tooltipsArrow.remove()
      }
    }
    function record_audio() {
      clearTooltips()
      if (navigator.mediaDevices) {
        navigator.mediaDevices.getUserMedia({ audio: true })
          .then(function(stream) {
            // get dimensions of outbound_message_textarea;
            const textarea = chatInputContainer.value
            const cover = outbound_message_cover.value
            const rect = textarea.getBoundingClientRect()
            // then cover it with the div contained in outbound_message_cover
            cover.style.top = `${textarea.offsetTop}px`
            cover.style.left = `${textarea.offsetLeft}px`
            cover.style.width = `${rect.width}px`
            cover.style.height = `${rect.height}px`
            cover.style.zIndex = 2147483647
            textarea.style.zIndex = -1
            cover.style.backgroundColor = 'white'
            cover.style.opacity = '0.5'
            cover.style.position = 'absolute'
            cover.style.border = '1px solid black'
            cover.style.borderRadius = '5px'
            cover.style.overflow = 'hidden'
            cover.style.textAlign = 'center'
            cover.style.lineHeight = `${rect.height}px`
            const chunks = []
            try {
              mediaRecorder = new MediaRecorder(stream)
              create_analyser(stream)
              mediaRecorder.start(1000)
              prettyStream.value = stream
            } catch (e) {
              alert(`mediaRecorder.start() failed: ${e}`)
            }
            recording.value = true 
            console.log(mediaRecorder.state)
            mediaRecorder.ondataavailable = function(e) {
              chunks.push(e.data)
            }
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            mediaRecorder.onstop = async function(e) {
              const blob = new Blob(chunks, { 'type' : codec.value })

              // uuencode the blob
              const reader = new FileReader()
              reader.onload = async function() {
                const dataUrl = reader.result
                const base64 = dataUrl.split(',')[1]
                const response = await fetchWrapper.post(`/transcribe/`, { audio: base64 })
                // eh wtf 
                chatInput.value.innerText = response.text
                chatInput.value.dispatchEvent(new Event('input', { bubbles: true }))
                // updateOutboundMessage()
              }
              reader.readAsDataURL(blob)
            }
          })
          .catch(function(err) {
            console.log('err.toString', err.toString())
            if (err.toString() === 'NotAllowedError: The request is not allowed by the user agent or the platform in the current context, possibly because the user denied permission.') {
              alert('Microphone permission denied.  Please enable microphone access by going to Settings; Lingo.ing; Microphone and enabling it')
            }
            console.log('The following getUserMedia error occured: ' + err)
          })
      } else {
        console.log('getUserMedia not supported on your browser!')
      }
    }
    function stop_recording() {
      prettyStream.value = false
      clearTooltips()
      console.log('stop_recording called')
      mediaRecorder.stop()
      recording.value = false
      const textarea = chatInputContainer.value
      textarea.style.zIndex = 'auto'

      const cover = outbound_message_cover.value
      cover.style.zIndex = -1
      cover.style.width = 0
      cover.style.height = 0
      console.log('stop_recording done')
    }
    function create_analyser(stream) {
      if (!audioCtx) {
        audioCtx = new AudioContext();
      }

      const source = audioCtx.createMediaStreamSource(stream);

      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 2048;

      source.connect(analyser);
      return
    }


  onMounted(async () => {
      await profileStore.fetchProfile(true)
      if (props.chatID) {
        chatID.value = props.chatID
      } else {
        const create_response = await fetchWrapper.post('/chat/create/', {
          templateID: props.templateID,
          game_sessionID: props.game_sessionID,
        curriculum_conceptID: props.curriculum_conceptID,
        curriculum_topicID: props.curriculum_topicID,
        curriculum_unitID: props.curriculum_unitID,
        flashcard_entryID: props.flashcard_entryID,
        vocab_entryID: props.vocab_entryID,
        recent_vocabID: props.recent_vocabID
      })
      chatID.value = create_response.chatID
      //article.value = create_response.article
    }
    const response = await fetchWrapper.post(`/chat/template/`, {
      // templateID: props.templateID,
      chatID: chatID.value
    })
    if (response.article) {
      article.value = response.article
      displayMode.value = 'article'
    }
    learning_language.value = response.learning_language
    for (const message of response.messages) {
      if (message.role == 'system') {
        continue
      }
      add_message(message)
    }
    if (messages.value.length == 0) {
      await get_ai_message()
    }
    scrollToBottom()
})

</script>

<style scoped>
/* For Webkit browsers (Chrome, Safari) */
.mini-chat-messages::-webkit-scrollbar {
  width: 8px;
}

.mini-chat-messages::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 2px;
}

.mini-chat-messages::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 2px;
}

.mini-chat-messages::-webkit-scrollbar-thumb:hover {
  background: #555;
}
/* For Firefox */
.mini-chat-messages {
  scrollbar-width: 8px;
  scrollbar-color: #32323290 #fff;
}

.mini-chat-container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.mini-chat-messages {
    flex-grow: 1;
    overflow-y: auto;
    overflow-x: hidden;
    font-size: 1em; /* Default font size */
}

.mini-chat-input {
  align-items: center;
  padding: 10px 29px 9px;
  margin-top: 10px;
  border-radius: 50px;
  border: 1px solid rgb(0 0 0 / 20%);
  box-shadow: 0 2px 5px rgb(0 0 0 / 20%);
}

div[contentEditable] {
  outline: none;
}

.chat-input {
  flex-grow: 1;
  text-align: start;
  margin-right: 10px;
  border: none;
  outline-width: 0;
  outline-offset: 2px;
  outline: 2px solid transparent;
}

.chat-input[contenteditable=true]:empty:not(:focus):before {
  content: attr(data-placeholder);
  color: #aaa;
  font-style: italic;
  pointer-events: none;
}

.chat-input[contenteditable=true]:focus:empty:before {
  content: attr(data-placeholder);
  color: #aaa;
  font-style: italic;
  pointer-events: none;
}
.action-icon {
    cursor: pointer;
    font-size: 200%;
    color: #007bff;
}
.bi-send-fill{
  color: #3260a9!important;
}
.disabled-icon {
    color: #ccc !important;
    cursor: not-allowed;
}


/* Styles for the popout menu */
.popout-menu {
    position: absolute;
    background: white;
    border: 1px solid #ccc;
    transform: translateY(-100%);
    border-radius: 5px;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
    padding: 10px;
    z-index: 1000;
}

.popout-menu ul {
    list-style: none;
    margin: 0;
    padding: 0;
}

.popout-menu li {
    padding: 5px 10px;
    cursor: pointer;
}

.popout-menu li:hover {
    background-color: #f1f1f1;
}

/* Suggestion pane styles */
.suggestion-pane {
    background: transparent;
}

.suggestion-heading {
    color: #495057;
    font-weight: 600;
    text-align: center;
    margin: 0;
    font-size: 1.1rem;
}

.suggestion-card {
    padding: 0 8px;
}

.suggestion-card-body {
    background: linear-gradient(135deg, #e8f4fd 0%, #d1ecf1 100%);
    border: 1px solid #bee5eb;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    min-height: 60px;
    display: flex;
    flex-direction: column;
    justify-content: center;
}

.suggestion-card-body:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 123, 255, 0.2);
    border-color: #17a2b8;
    background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
}

.suggestion-translation {
    font-style: italic;
    font-size: 0.85em;
    color: #6c757d;
    margin-top: 4px;
}
</style>
